// 9fbef606107a605d69c0edbcd8029e5d
import { executeExternalAPICall } from '@tcp/core/src/services/handler';
import { getAPIConfig, parseBoolean } from '@tcp/core/src/utils';
import { executeStatefulAPICall } from '../../handler';
import endpoints from '../../endpoints';
import { sanitizeEntity, parseStoreHours } from '../../../utils';
import { formatPhoneNumber } from '../../../utils/formValidation/phoneNumber';

export const ERROR_MESSAGES_BOPIS = {
  caPostalCode: 'Please enter a Canadian Postal Code',
  usZipCode: 'Please enter a US Zip Code',
  zeroResults: 'ZERO_RESULTS',
  noAddressFound: 'We were unable to find the address you typed, please try again',
  selectSize: 'Please select a size',
  storeSearchException: 'Oops, something went wrong, Please retry',
  apiTimeoutError: 'Oh no! Something went wrong… Please try again in a few minutes',
  storeNotFound: 'No Stores Found',
};

export const BOPIS_ITEM_AVAILABILITY = {
  AVAILABLE: 'OK',
  LIMITED: 'LIMITED',
  UNAVAILABLE: 'UNAVAILABLE',
};

export const STORE_TYPES = {
  RETAIL: 'Retail Store',
  OUTLET: 'Outlet',
};

const shouldFetchAddressLine = (store) => {
  return (
    store.addressLine && typeof store.addressLine === 'object' && !Array.isArray(store.addressLine)
  );
};

const fetchStoreType = (store, addressLine) => {
  return (
    store.storeType || (addressLine && addressLine[addressLine.length - 1]) || store.address3 || ''
  );
};

const getHoursOfOperation = (store, isFromDeafultCase) => {
  let hoursOfOperation;
  const storeHours = store?.storehours || JSON.parse(store?.storeHours);
  // Backend's API structure for stores are never the same, so i am checking a few differant places for store hours
  if (storeHours) {
    if (isFromDeafultCase) {
      hoursOfOperation = storeHours;
    } else {
      hoursOfOperation = storeHours.storehours;
    }
  } else if (store.Attribute && store.Attribute[0]) {
    hoursOfOperation = JSON.parse(store.Attribute[0].displayValue || '{}').storehours;
  } else if (store.attribute) {
    hoursOfOperation = JSON.parse(store.attribute.displayValue || '{}').storehours;
  }
  return hoursOfOperation;
};

const getStoreType = (storeType) => {
  return STORE_TYPES[storeType] || (storeType === 'PLACE' && STORE_TYPES.RETAIL) || '';
};

const basicInfoId = (store) => {
  return (
    store.uniqueID ||
    store.uniqueId ||
    store.storeLocId ||
    store.storeUniqueID ||
    store.stLocId ||
    store.id
  ).toString();
};

const getIsDefault = (preferredStore) => {
  return preferredStore || false;
};

const getStoreAddress = (store) => {
  return store.address1 || store.streetLine1 || (store.addressLine && store.addressLine[0]);
};

const getState = (store) => {
  return store.stateOrProvinceName || store.state || store.stateCode;
};

const getZipcode = (store) => {
  return (store.zipCode || store.postalCode || store.postalcode || store.zipcode).toString().trim();
};

const getStoreName = (store) => {
  return (
    (store.description && store.description.displayStoreName) ||
    (store.Description && store.Description[0] && store.Description[0].displayStoreName) ||
    store.storeName ||
    store.name ||
    ''
  );
};
const getBasicInfo = (store, isFromDefaultList) => {
  let phoneNumber = store.telephone1 || store.phone || store.phone1 || store.telephone || '';
  if (typeof phoneNumber === 'number') {
    phoneNumber = phoneNumber.toString();
  }
  let address = getStoreAddress(store);
  if (isFromDefaultList) {
    address = store.addressLine;
  }

  return {
    id: basicInfoId(store),
    storeName: sanitizeEntity(getStoreName(store)),
    isDefault: getIsDefault(store.preferredStore),
    address: {
      addressLine1: sanitizeEntity(address),
      city: store.city,
      state: getState(store),
      country: store.country || store.country_code,
      zipCode: getZipcode(store),
    },
    phone: formatPhoneNumber(phoneNumber),
    coordinates: {
      lat: parseFloat(store.latitude),
      long: parseFloat(store.longitude),
    },
    storeDiscount: (store.cStoreDiscount || store.storeDiscount)?.toString(),
  };
};

const getProductAvailability = (store, altProductAvailability1, requestedQuantity) => {
  const itemAvailability =
    (store?.itemAvailability && store?.itemAvailability[0]) || store?.inventory;
  return itemAvailability
    ? {
        skuId: itemAvailability?.itemId,
        status:
          itemAvailability?.qty < requestedQuantity
            ? BOPIS_ITEM_AVAILABILITY.UNAVAILABLE
            : altProductAvailability1,
        quantity: itemAvailability.qty,
        availableProductCountInStore: itemAvailability?.totalProductAvailableInStore,
        availableProducts: itemAvailability?.availableProducts,
        totalItemsInShipment: itemAvailability?.totalItemsInShipment,
        isStoreSpecial: itemAvailability?.isStoreSpecial || false,
      }
    : {};
};

/** @function storeAPIParser
 *   @summary This is the main function that should be used when trying to parse backends apis that return store information
 *   @param {Object} store - The store object exactly how backend sends it in their API
 *   @param {Object} configs - options to apply, somtimes we want some conditionaly set values
 *   @param {Number} configs.requestedQuantity - if passed this will affect a store's product status flag if not passed we will take the as its status from the API, (itemAvailability.qty < requestedQuantity) ? AVAILABILITY.UNAVAILABLE : AVAILABILITY.OK
 */

export function storeAPIParser(store, configs = { requestedQuantity: 0 }) {
  const { requestedQuantity } = configs;
  let addressLine;
  // Sometimes addressLine is returned as an array
  // Sometimes addressLine is returned as an object with numerical properties (WHY???)
  // If addressLine is object, convert to array
  if (shouldFetchAddressLine(store)) {
    addressLine = Object.keys(store.addressLine).map((key) => store.addressLine[key]);
  }

  // Sometimes storeType is explicitly defined
  // Sometimes storeType needs to be determined using address
  const storeType = fetchStoreType(store, addressLine);
  const hoursOfOperation = getHoursOfOperation(store);

  const distanceAlt = store.distanceFromUserToStore
    ? parseFloat(store.distanceFromUserToStore).toFixed(2)
    : null;
  const altProductAvailability2Status =
    (store?.itemAvailability && store?.itemAvailability[0]) || store?.inventory;
  const altProductAvailability2 =
    altProductAvailability2Status && altProductAvailability2Status?.itemStatus === 'UNAVAILABLE'
      ? BOPIS_ITEM_AVAILABILITY.UNAVAILABLE
      : BOPIS_ITEM_AVAILABILITY.LIMITED;

  const altProductAvailability1 =
    altProductAvailability2Status && altProductAvailability2Status?.itemStatus === 'AVAILABLE'
      ? BOPIS_ITEM_AVAILABILITY.AVAILABLE
      : altProductAvailability2;

  // Parse Store Info
  const storeFilteredInfo = {
    // Boss store info
    storeBossInfo: {
      isBossEligible: parseBoolean(store.cIsStoreBossEligible),
      startDate: store.bossMinDate,
      endDate: store.bossMaxDate,
    },
    /** added storeType | also checking if the flag is undefined than the value should be true
     * for default searching without any restriction
     */
    pickupType: {
      isStoreBossSelected:
        store.isStoreBossSelected !== undefined ? store.isStoreBossSelected : true,
      isStoreBopisSelected:
        store.isStoreBopisSelected !== undefined ? store.isStoreBopisSelected : true,
    },
    distance: store.distance ? parseFloat(store.distance).toFixed(2) : distanceAlt,
    basicInfo: getBasicInfo(store),
    hours: {
      regularHours: [],
      holidayHours: [],
      regularAndHolidayHours: [],
    },
    features: {
      storeType: getStoreType(storeType),
      mallType: store.x_mallType,
      entranceType: store.x_entranceType,
    },
    productAvailability: getProductAvailability(store, altProductAvailability1, requestedQuantity),
  };

  // Parse Store Hours
  if (hoursOfOperation && hoursOfOperation.length) {
    storeFilteredInfo.hours.regularHours = parseStoreHours(hoursOfOperation);
  }
  if (store?.inventory?.atcStatus) {
    storeFilteredInfo.atcStatus = store?.inventory?.atcStatus;
  }

  return storeFilteredInfo;
}

export const storeResponseParserSfcc = (storeData) => {
  const {
    c_MAX_DATE: MAX_DATE,
    c_MIN_DATE: MIN_DATE,
    address1: addressLine,
    c_IS_STORE_BOSS_ELIGIBLE: bosseligible,
    c_brand: brand,
    city,
    country_code: country,
    store_hours: storehoursjson,
    c_DC_PROCESSING_TIME: dcprocessingtime,
    c_entranceType: entranceType,
    latitude,
    longitude,
    c_mallType: mallType,
    c_MAX_NUMBER_OF_DAYS: maxdays,
    c_MIN_NUMBER_OF_DAYS: mindays,
    c_SHIP_SCHEDULE_FRI: SHIP_SCHEDULE_FRI,
    c_SHIP_SCHEDULE_MON: SHIP_SCHEDULE_MON,
    c_SHIP_SCHEDULE_SAT: SHIP_SCHEDULE_SAT,
    c_SHIP_SCHEDULE_SUN: SHIP_SCHEDULE_SUN,
    c_SHIP_SCHEDULE_THU: SHIP_SCHEDULE_THU,
    c_SHIP_SCHEDULE_TUE: SHIP_SCHEDULE_TUE,
    c_SHIP_SCHEDULE_WED: SHIP_SCHEDULE_WED,
    state_code: state,
    id: storeLocId,
    name: storeName,
    c_STORE_BRAND_TYPE: storeType,
    c_STORE_PROCESSING_TIME: storeprocessingtime,
    phone: telephone,
    postal_code: zipCode,
    distance,
    cStoreDiscount: storeDiscount,
  } = storeData;
  return {
    properties: {
      MAX_DATE,
      MIN_DATE,
      addressLine,
      bosseligible: bosseligible === '1' ? 'Y' : 'N',
      brand,
      city,
      country,
      storehoursjson,
      dcprocessingtime,
      entranceType,
      latitude,
      longitude,
      mallType,
      maxdays,
      mindays,
      shipschedule: `SHIP_SCHEDULE_FRI:${SHIP_SCHEDULE_FRI};SHIP_SCHEDULE_MON:${SHIP_SCHEDULE_MON};SHIP_SCHEDULE_SAT:${SHIP_SCHEDULE_SAT};SHIP_SCHEDULE_SUN:${SHIP_SCHEDULE_SUN};SHIP_SCHEDULE_THU:${SHIP_SCHEDULE_THU};SHIP_SCHEDULE_TUE:${SHIP_SCHEDULE_TUE};SHIP_SCHEDULE_WED:${SHIP_SCHEDULE_WED}`,
      state,
      storeLocId,
      storeName,
      storeType,
      storeprocessingtime,
      telephone,
      zipCode,
      tilequery: {
        distance,
        geometry: '',
        layer: '',
      },
      storeDiscount,
    },
  };
};

function defaultStoresAPIParser(store, defaultStoreId) {
  if (!store || !store.properties) {
    return {};
  }
  const storeObject = store.properties;
  const tileQueryObj = storeObject.tilequery;

  let addressLine;
  if (shouldFetchAddressLine(storeObject)) {
    addressLine = Object.keys(store.addressLine).map((key) => store.addressLine[key]);
  }
  const storeType = fetchStoreType(storeObject, addressLine);
  let storeJson;
  if (storeObject) {
    if (storeObject.storehours) {
      storeJson = storeObject.storehours;
    } else if (storeObject.storehoursjson) {
      storeJson = storeObject.storehoursjson;
    } else {
      return {};
    }
  }
  const hoursOfOperation = getHoursOfOperation(JSON.parse(storeJson), true);
  const distanceAlt = null;
  // Parse Store Info
  const storeFilteredInfo = {
    // Boss store info
    storeBossInfo: {
      isBossEligible: true,
      startDate: '',
      endDate: '',
    },

    /** added storeType | also checking if the flag is undefined than the value should be true
     * for default searching without any restriction
     */
    pickupType: {
      isStoreBossSelected: true,
      isStoreBopisSelected: true,
    },
    distance: tileQueryObj.distance ? parseFloat(tileQueryObj.distance).toFixed(2) : distanceAlt,
    basicInfo: getBasicInfo(storeObject, true),
    hours: {
      regularHours: [],
      holidayHours: [],
      regularAndHolidayHours: [],
    },
    features: {
      storeType: getStoreType(storeType),
      mallType: store.x_mallType,
      entranceType: store.x_entranceType,
    },
    productAvailability: {},
  };
  if (
    defaultStoreId &&
    storeObject.storeLocId &&
    Number(storeObject.storeLocId) === Number(defaultStoreId)
  ) {
    storeFilteredInfo.basicInfo = { ...storeFilteredInfo.basicInfo, isDefault: true };
  }
  // Parse Store Hours
  if (hoursOfOperation && hoursOfOperation.length) {
    storeFilteredInfo.hours.regularHours = parseStoreHours(hoursOfOperation);
  }

  return storeFilteredInfo;
}

const getErrorFromResponse = (err) => {
  let apiError = '';
  if (err && err === ERROR_MESSAGES_BOPIS.zeroResults) {
    apiError = { error: ERROR_MESSAGES_BOPIS.noAddressFound };
  }
  if (err && err.errors) {
    apiError = { error: err.errors };
  }
  return apiError;
};

export const getDefaultStoresPlusInventorybyLatLngMapBox = ({
  lat,
  lng,
  defaultStore,
  distance,
  isModalOpeningFromAvailabilityFilter,
}) => {
  let defaultStoreId = null;
  if (defaultStore && defaultStore.basicInfo) {
    defaultStoreId = defaultStore.basicInfo.id;
  }
  const selectedDistance = distance || 75;
  const DEFAULT_RADIUS = isModalOpeningFromAvailabilityFilter ? selectedDistance : 75;
  const apiUrl = `${
    getAPIConfig().mapboxStoreLocatorApiURL
  }/childrensplace.store-locator/tilequery/${lng},${lat}.json?radius=${
    DEFAULT_RADIUS * 1000
  }&limit=${25}&access_token=${getAPIConfig().mapBoxApiKey}`;
  const webserviceInfo = {
    URI: apiUrl,
    method: 'GET',
  };

  const payload = {
    webService: webserviceInfo,
  };

  let apiError = { error: ERROR_MESSAGES_BOPIS.noAddressFound };
  return executeExternalAPICall(payload)
    .then((res) => {
      const stores = res.body.features || [];
      if (stores && stores.length) {
        return {
          stores: stores.map((store) => defaultStoresAPIParser(store, defaultStoreId)),
        };
      }
      apiError = { error: ERROR_MESSAGES_BOPIS.noAddressFound };
      return { error: apiError.error, stores: [] };
    })
    .catch((err) => {
      apiError = getErrorFromResponse(err);
    });
};

export const getLocationStoresSfcc = ({
  lat,
  lng,
  defaultStore,
  distance,
  isModalOpeningFromAvailabilityFilter,
}) => {
  if (!lat || !lng) {
    return {};
  }
  const selectedDistance = distance || 75;
  const DEFAULT_RADIUS = isModalOpeningFromAvailabilityFilter ? selectedDistance : 75;
  let defaultStoreId = null;
  if (defaultStore && defaultStore.basicInfo) {
    defaultStoreId = defaultStore.basicInfo.id;
  }
  const { URI, method } = endpoints.sfccStoreLocatorApiURL;
  let apiError = { error: ERROR_MESSAGES_BOPIS.noAddressFound };
  const apiUrl = `${URI}?distance_unit=mi&max_distance=${DEFAULT_RADIUS}&latitude=${lat}&longitude=${lng}&count=${25}`;
  const payload = {
    webService: {
      URI: apiUrl,
      method,
    },
  };

  return executeStatefulAPICall(payload, null, true)
    .then((res) => {
      const fetchedStores = res.body.data || [];
      if (fetchedStores && fetchedStores.length) {
        return {
          stores: fetchedStores.map((store) =>
            defaultStoresAPIParser(storeResponseParserSfcc(store), defaultStoreId)
          ),
        };
      }
      apiError = { error: ERROR_MESSAGES_BOPIS.noAddressFound };
      return { error: apiError.error, stores: [] };
    })
    .catch((err) => {
      apiError = getErrorFromResponse(err);
    });
};
